import React from 'react'

/**
 * 肯定と否定の文言を論理型に変換する
 * @param value
 * @param defaultValue
 * @returns
 */
export function toBoolean(value: string | boolean | null | undefined, defaultValue: boolean | undefined = undefined): boolean | undefined {
  switch (value) {
    case true:
    case 'true':
    case '1':
    case 'yes':
    case 'on': {
      return true
    }
    case false:
    case 'false':
    case '0':
    case 'no':
    case 'off': {
      return false
    }
    default:
      return defaultValue
  }
}

/**
 * 文字列を数値に変換する
 * Number()だと null や '' が 0 に、undefined が NaN に変換されるので、想定外の値を制御する
 * valueが数字の文字列以外なら、defaultValue を返す
 * @param value
 * @param defaultValue
 * @returns
 */
export function toNumber(value: string | undefined | null, defaultValue: number | undefined = undefined): number | undefined {
  if (value) {
    const num = Number(value)
    return isNaN(num) ? defaultValue : num
  } else {
    return defaultValue
  }
}

/**
 * 特定のプリミティブ値を列挙型にキャストする
 * @param value
 * @returns
 */
export function autoCast<T>(value: string | number): T {
  const num = Number(value)
  if (isNaN(num)) {
    return value as T
  } else {
    return num as T
  }
}

/**
 * 特定のプリミティブ値を列挙型にキャストする(Undefined許容)
 * @param value
 * @returns
 */
export function autoCastOrUndefined<T>(value: string | number | undefined): T | undefined {
  if (value != null) {
    return autoCast(value)
  } else {
    return value
  }
}

/**
 * 入力値の設定クラス
 * @param key
 * @param event
 */
export class PropertySetter<T> {
  private setter: React.Dispatch<React.SetStateAction<T>>

  constructor(setter: React.Dispatch<React.SetStateAction<T>>) {
    this.setter = setter
  }

  public set(key: keyof T, event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLTextAreaElement>) {
    this.setter((values) => ({ ...values, [key]: event.target.value }))
  }
}

/**
 * Number Format
 * @param value
 * @returns
 */
export const numberFormat = (value: number | undefined | null) => {
  if (value != null) {
    const formatter = new Intl.NumberFormat()
    return formatter.format(value)
  } else {
    return '-'
  }
}

interface DateMap {
  M: number
  d: number
  h: number
  m: number
  s: number
}
const formatDate = (date: Date | undefined | null, format: string) => {
  if (date && !isNaN(date.getTime())) {
    const symbol: DateMap = {
      M: date.getMonth() + 1,
      d: date.getDate(),
      h: date.getHours(),
      m: date.getMinutes(),
      s: date.getSeconds(),
    }
    const formatted = format.replace(/(M+|d+|h+|m+|s+)/g, (v) => {
      const key = v.slice(-1) as keyof DateMap
      const val = String(symbol[key] ?? '')
      return `${v.length > 1 ? '0' : ''}${val}`.slice(-2)
    })
    return formatted.replace(/(y+)/g, (v) => date.getFullYear().toString().slice(-v.length))
  } else {
    return '-'
  }
}

/**
 * 日付フォーマッター
 * @param date 日付
 * @param format 例) yyyy/MM/dd
 * @returns
 */
export const dateFormatter = (date: Date | string | undefined | null, format: string) => {
  if (typeof date === 'string') {
    return formatDate(new Date(date), format)
  } else {
    return formatDate(date, format)
  }
}

/**
 * 生年月日フォーマッター
 * @param birthday 生年月日
 * @returns
 */
export const birthdayFormatter = (birthday?: string) => {
  if (birthday) {
    const year = parseInt(birthday.substring(0, 4), 10)
    const month = parseInt(birthday.substring(4, 6), 10)
    const day = parseInt(birthday.substring(6, 8), 10)
    return `${year}/${month}/${day}`
  } else {
    return '-'
  }
}

/**
 * 正規表現エスケープ
 * @param str
 * @returns
 */
export const regulationEscape = (str: string) => {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}
